/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the base layout for the browse view.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

/**
 * Utility used to generate a Hook to allow for the creation of styles using JSS.
 */
import { makeStyles } from '@material-ui/core/styles';

/**
 * Material UI Paper component.
 */
import Paper from '@material-ui/core/Paper';

/**
 * Material UI List component.
 */
import List from '@material-ui/core/List';

/**
 * Material UI Link component.
 */
import Link from '@material-ui/core/Link';

/**
 * Material UI Typography component.
 */
import { Typography } from '@material-ui/core';

/**
 * Material UI Breadcrumbs component.
 */
import Breadcrumbs from '@material-ui/core/Breadcrumbs';

/**
 * Typings
 */
import { RouteChildrenProps} from 'react-router';

/**
 * Used to navigate around the app.
 */
import { Link as RouterLink } from 'react-router-dom';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/**
 * Enables the use of term data loaded from the ServiceStack API.
 */
import useTermsByCategory from 'src/hooks/useTermsByCategory';

/**
 * Enables the use of term data loaded from the ServiceStack API.
 */
import useTermsByTerm from 'src/hooks/useTermsByTerm';

/**
 * Enumeration
 */
import { RequestState } from 'src/store/reducers';

/**
 * used to render the view for each term item.
 */
import TermListItem from 'src/components/TermListItem';

/**
 * Loading indicator
 */
import Loading from 'src/components/Loading';

/**
 * Used to limit results to the current version only.
 */
import useVersionCheck from 'src/hooks/useVersionCheck';

/**
 * used to update app state and post messages to opener window.
 */
import useAppState from 'src/hooks/useAppState';

/**
 * Enumeration
 */
import { WindowView } from 'src/utils/MessageManager';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces / Types
 * ---------------------------------------------------------------------------------
 */

interface IBrowseTermsParams {
    categoryId: string;
    termId: string;
}

interface IBrowseTermsProps extends RouteChildrenProps<IBrowseTermsParams> {

}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles(theme => ({
    title: {
        margin: theme.spacing(3, 0)
    },
    paper: {
        margin: theme.spacing(3, 0)
    },
    list: {
        width: '100%',
        padding: theme.spacing(0,0)
    },
    breadcrumbs: {
        margin: theme.spacing(3, 0)
    }
}));

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

// const limit = 5;

/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */

const BrowseTerms: React.FunctionComponent<IBrowseTermsProps> = ({
    match
}) => {
    const classes = useStyles();

    if (!match || (!match.params.termId && !match.params.categoryId)) {
        return null;
    }

    const [ version, category, term, view, loadState ] = match.params.termId ?
        useTermsByTerm(parseInt(match.params.termId, 10)) :
        useTermsByCategory(parseInt(match.params.categoryId, 10));

    useVersionCheck(version);

    useAppState(
        WindowView.Browse,
        version,
        category,
        term,
        null,
        null
    );

    const loading = loadState && loadState.state === RequestState.Pending;

    return (
        <>
            {
                version && category && !term ?
                    <Breadcrumbs
                        className={classes.breadcrumbs}
                        separator="›"
                        aria-label="Breadcrumb"
                    >
                        <Link
                            component={RouterLink}
                            to={`/${version.code}/browse`}
                        >
                            Categories
                        </Link>
                        <Typography color="textPrimary">
                            {category.name}
                        </Typography>
                    </Breadcrumbs> :
                    version && category && term ?
                        <Breadcrumbs
                            className={classes.breadcrumbs}
                            separator="›"
                            aria-label="Breadcrumb"
                        >
                            <Link
                                component={RouterLink}
                                to={`/${version.code}/browse`}
                            >
                                Categories
                            </Link>
                            <Link
                                component={RouterLink}
                                to={`/${version.code}/browse/category/${category.id}`}
                            >
                                {category.name}
                            </Link>
                            <Typography color="textPrimary">
                                {term.name}
                            </Typography>
                        </Breadcrumbs> :
                        <Breadcrumbs
                            className={classes.breadcrumbs}
                            separator="›"
                            aria-label="Breadcrumb"
                        >
                            <Typography>
                                Categories
                            </Typography>
                        </Breadcrumbs>
            }
            <Paper
                className={classes.paper}
            >
                {
                    loading && (
                        <Loading />
                    )
                }
                <List
                    className={classes.list}
                >
                    {
                        version && category && view.map((termId, index) => (
                            <TermListItem
                                key={termId}
                                version={version}
                                category={category}
                                parentTerm={term}
                                termId={termId}
                                showDivider={index !== termId}
                            />
                        ))
                    }
                </List>
            </Paper>
        </>
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Exports - Default
 * ---------------------------------------------------------------------------------
 */

export default BrowseTerms;


// WEBPACK FOOTER //
// ./src/containers/BrowseTerms.tsx